// Algorytm powtórek (SPEC §23.13): z całej bazy słów wybiera te, które mają być powtórzone dziś — także gdy po wielu dniach jest ich tysiące.
// Czysty kod bez wejścia/wyjścia. Nie zmienia pudełek Leitnera (to robi `srs.ts: review`) — tylko DECYDUJE, które słowa i jakim ćwiczeniem.
//
// Zasady:
//  1. Dzienny dziennik nauki (`DayLogs`): dla każdego dnia — ile lekcji i które słowa (wszystkie lekcje dnia razem).
//  2. „Pula świeża” = słowa z ostatnich dni nauki (domyślnie 1 dzień, wolne dni się nie liczą), WSZYSTKICH lekcji tego dnia, nie tylko pierwszej.
//  3. Miejsca na powtórki (`limit`, domyślnie 5 na lekcję) dzielą się: ok. 60% z puli świeżej (losowo), reszta z zaległych starszych
//     (według względnego spóźnienia). Gdy jednej kategorii brakuje, jej miejsca zajmuje druga.
//  4. Względne spóźnienie = dni od ostatniej powtórki ÷ zaplanowany odstęp słowa (≥ 1 = termin minął). Słowo z pudełka 1 spóźnione o dzień (2,0) wyprzedza
//     słowo z pudełka 5 spóźnione o 10 dni (1,33): wczesne powtórki są najcenniejsze, a spóźnienie starego słowa kosztuje mniej.
//  5. Remisy rozstrzyga losowanie z ziarna (profil + data + numer lekcji): przeładowanie ekranu nie zmienia zestawu, a kolejne lekcje dnia dostają
//     inne słowa (wykluczamy już powtórzone dziś).
//  6. Nic nie jest „kolejką do odrobienia”: słowa, które się nie zmieściły, zostają zaległe i następnego dnia mają wyższe względne spóźnienie.

import { daysBetween } from './dates.ts';
import { mulberry32 } from './rng.ts';
import type { SrsItem } from '../types.ts';

export interface ReviewConfig {
  /** Powtórek w rozgrzewce jednej lekcji (kolejne lekcje dnia dostają kolejne, inne słowa). */
  perLesson: number;
  /** Udział puli świeżej (słowa z ostatnich dni nauki) wśród miejsc na powtórki. */
  freshShare: number;
  /** Ile ostatnich DNI NAUKI (nie kalendarzowych) wchodzi do puli świeżej. */
  freshDays: number;
  /**
   * Górna granica dziennej sesji „Powtórka dnia”: tyle powtórek na każde nowe słowo dziennie (średnio z ostatnich dni nauki) — 5 nowych słów dziennie = 40.
   * To GRANICA, nie cel: sesja ma tyle słów, ile jest zaległych (na początku kursu kilka, po pół roku ok. 30 przy 5 nowych dziennie); symulacja pokazała, że
   * granica 6× (30) zaczyna dławić bazę po ok. 230 dniach, a dołożenie jej dopiero wtedy nie nadrabia zaległości — dlatego 8× od pierwszego dnia.
   */
  sessionPerNewWord: number;
  /** Dolna i górna granica rozmiaru sesji (słów); rodzic może ją zmienić w panelu. */
  sessionMin: number;
  sessionMax: number;
  /** Z ilu ostatnich dni nauki liczymy średnią liczbę nowych słów dziennie. */
  sessionWindow: number;
  /** O ile pudełek spada słowo po błędzie (min. pudełko 1) — polityka `srs.ts: ReviewPolicy`. */
  lapseDrop: number;
  /** Odstępy pudełka 5 przy kolejnych udanych powtórkach (dni); ostatni obowiązuje dalej. */
  box5: readonly number[];
}

export const defaultReviewConfig: ReviewConfig = { perLesson: 5, freshShare: 0.6, freshDays: 1, sessionPerNewWord: 8, sessionMin: 20, sessionMax: 120, sessionWindow: 7, lapseDrop: 2, box5: [30, 75, 180] };

/** Dziennik jednego dnia nauki: ile lekcji skończono i które słowa poznano (wszystkie lekcje dnia). */
export interface DayLog {
  lessons: number;
  words: string[];
}
export type DayLogs = Record<string, DayLog>;

/** Dopisuje skończoną lekcję do dziennika dnia (bez duplikatów słów, kolejność zachowana). */
export function recordLesson(log: DayLogs, date: string, words: readonly string[]): DayLogs {
  const day = log[date] ?? { lessons: 0, words: [] };
  const merged = [...day.words];
  for (const w of words) if (!merged.includes(w)) merged.push(w);
  return { ...log, [date]: { lessons: day.lessons + 1, words: merged } };
}

/** Słowa z `days` ostatnich dni nauki przed `today` (dni bez lekcji pomijamy). */
export function freshPool(log: DayLogs, today: string, days: number): string[] {
  const dates = Object.keys(log)
    .filter((d) => d < today && log[d]!.words.length > 0)
    .sort()
    .slice(-Math.max(1, days));
  const out: string[] = [];
  for (const d of dates) for (const w of log[d]!.words) if (!out.includes(w)) out.push(w);
  return out;
}

/**
 * Górna granica dziennej sesji „Powtórka dnia”: `sessionPerNewWord` × średnia liczba nowych słów w ostatnich `sessionWindow` dniach nauki (przed dziś),
 * zaokrąglona do 5 i obcięta do [`sessionMin`, `sessionMax`]. Bez historii liczymy 5 nowych słów dziennie (jedna lekcja). Sesja jest dzielona
 * na porcje po 10 w ekranie, więc dziecko może skończyć wcześniej — to, co się nie zmieściło, rośnie w kolejce (względne spóźnienie).
 */
export function sessionSize(log: DayLogs, today: string, cfg: ReviewConfig = defaultReviewConfig): number {
  const dates = Object.keys(log)
    .filter((d) => d < today && log[d]!.words.length > 0)
    .sort()
    .slice(-Math.max(1, cfg.sessionWindow));
  const perDay = dates.length === 0 ? 5 : dates.reduce((n, d) => n + log[d]!.words.length, 0) / dates.length;
  const raw = Math.round((perDay * cfg.sessionPerNewWord) / 5) * 5;
  return Math.max(cfg.sessionMin, Math.min(cfg.sessionMax, raw));
}

/**
 * Względne spóźnienie: dni od ostatniej powtórki ÷ odstęp, jaki to słowo miało zaplanowany (due − lastSeen; to uwzględnia rosnący odstęp pudełka 5).
 * 1,0 = dokładnie w terminie; < 1 = jeszcze nie czas. Gdy termin jest niespójny (due ≤ lastSeen), liczymy z odstępu pudełka.
 */
export function relativeOverdue(item: SrsItem, today: string, intervals: Record<1 | 2 | 3 | 4 | 5, number>): number {
  const planned = daysBetween(item.lastSeen, item.due);
  const interval = planned > 0 ? planned : Math.max(1, intervals[item.box]);
  return Math.max(0, daysBetween(item.lastSeen, today)) / interval;
}

export interface PlanArgs {
  items: Record<string, SrsItem>;
  today: string;
  log: DayLogs;
  /** Ile słów wybrać; domyślnie `cfg.perLesson`. */
  limit?: number;
  /** Słowa wykluczone: dzisiejsza lekcja i to, co już powtórzono dziś. */
  exclude?: ReadonlySet<string>;
  /** Ziarno losowania (np. skrót z identyfikatora profilu, daty i numeru lekcji dnia). */
  seed: number;
  intervals: Record<1 | 2 | 3 | 4 | 5, number>;
  cfg?: ReviewConfig;
}

export interface ReviewPlan {
  words: string[];
  /** Ile z wybranych pochodzi z puli świeżej, a ile z zaległych starszych. */
  fresh: number;
  scheduled: number;
  /** Ile słów jest zaległych w sumie (przed wyborem) — do wskaźnika „zaległości” na ekranie głównym. */
  dueTotal: number;
}

/** Sortuje malejąco po względnym spóźnieniu; remisy (z dokładnością do 1e-9) rozstrzyga losowanie z ziarna. */
function rank(list: readonly SrsItem[], today: string, intervals: PlanArgs['intervals'], rnd: () => number): SrsItem[] {
  return list
    .map((it) => ({ it, score: relativeOverdue(it, today, intervals), tie: rnd() }))
    .sort((a, b) => (Math.abs(a.score - b.score) > 1e-9 ? b.score - a.score : a.tie - b.tie))
    .map((x) => x.it);
}

export function planReviews(args: PlanArgs): ReviewPlan {
  const cfg = args.cfg ?? defaultReviewConfig;
  const limit = Math.max(0, args.limit ?? cfg.perLesson);
  const exclude = args.exclude ?? new Set<string>();
  const rnd = mulberry32(args.seed);
  const due = Object.values(args.items).filter((it) => it.due <= args.today && !exclude.has(it.word));
  const dueTotal = due.length;
  if (limit === 0 || dueTotal === 0) return { words: [], fresh: 0, scheduled: 0, dueTotal };

  const pool = new Set(freshPool(args.log, args.today, cfg.freshDays));
  const freshDue = rank(due.filter((it) => pool.has(it.word)), args.today, args.intervals, rnd);
  const olderDue = rank(due.filter((it) => !pool.has(it.word)), args.today, args.intervals, rnd);

  const wantFresh = Math.round(limit * cfg.freshShare);
  let nFresh = Math.min(wantFresh, freshDue.length);
  let nOlder = Math.min(limit - nFresh, olderDue.length);
  // brakuje starszych → resztę zajmują świeże; brakuje świeżych → resztę zajmują starsze
  if (nFresh + nOlder < limit) nFresh = Math.min(freshDue.length, limit - nOlder);
  const words = [...freshDue.slice(0, nFresh), ...olderDue.slice(0, nOlder)].map((it) => it.word);
  return { words, fresh: nFresh, scheduled: nOlder, dueTotal };
}

/** Rodzaje ćwiczeń powtórkowych (od łatwego rozpoznawania do mówienia). */
export type ReviewKind = 'picture' | 'choose' | 'cloze' | 'say';

/** Co jest dostępne dla słowa w treści lekcji (nie każde słowo ma pytanie do obrazka czy kartę „Co powiesz?”). */
export interface Available {
  picture: boolean;
  say: boolean;
}

/**
 * Drabinka ćwiczeń wg pudełka: 1–2 rozpoznawanie (pytanie do obrazka, a gdy go brak — wybór znaczenia), 3 uzupełnianie zdania, 4–5 mówienie
 * („Co powiesz?”); gdy brakuje danych dla słowa — najbliższe łatwiejsze ćwiczenie, które każde słowo ma (uzupełnianie zdania). Zgodnie z SPEC §23.13.2 pkt 7;
 * używa jej „Powtórka dnia” kursu v2 (`sessionV2.ts: buildReviewSession`, patch-47).
 */
export function reviewKind(box: SrsItem['box'], available: Available): ReviewKind {
  if (box <= 2) return available.picture ? 'picture' : 'choose';
  if (box === 3) return 'cloze';
  return available.say ? 'say' : 'cloze';
}

/**
 * Prognoza obciążenia: ile powtórek dziennie wynika z tempa nauki w stanie ustalonym (bez opóźnień i pomyłek) — do rozmów o limitach.
 * Dzień, w którym słowo poznane `a` dni temu jest powtarzane, to suma kolejnych odstępów (1, 1+3, 1+3+7, 1+3+7+14, …); obciążenie dnia to
 * nowe słowa dziennie × liczba takich terminów, które zdążyły nastąpić. Przy rosnącym `box5` obciążenie rośnie z logarytmem wieku, nie liniowo.
 */
export function steadyLoadPerDay(newPerDay: number, ageDays: number, intervals: Record<1 | 2 | 3 | 4 | 5, number>, box5: readonly number[] = [intervals[5]]): number {
  const marks: number[] = [];
  let t = 0;
  for (const b of [1, 2, 3, 4] as const) {
    t += intervals[b];
    marks.push(t);
  }
  // pudełko 5: pierwszy odstęp z drabinki po dojściu do pudełka 5, potem kolejne; ostatni powtarza się bez końca
  let k = 0;
  while (t <= ageDays && marks.length < 400) {
    t += box5[Math.min(k, box5.length - 1)]!;
    marks.push(t);
    k++;
  }
  return newPerDay * marks.filter((m) => m <= ageDays).length;
}
